import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Maximize2, Minimize2, RotateCcw, Download, Plus, X, ChevronRight,
  Heart, Sparkles, Target, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';
import {
  FeelIcon, NeedIcon, SaveIcon, ArrowLeftIcon, ArrowRightIcon
} from '../icons/ElchmeIcons';

export default function CompassionateWheelInteractive() {
  const navigate = useNavigate();
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [activeRing, setActiveRing] = useState(null); // 'needs', 'feelings', 'strategies'
  const [activePerson, setActivePerson] = useState('self'); // 'self' or 'other'
  const [showFinalWheel, setShowFinalWheel] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newItemText, setNewItemText] = useState('');

  // Wheel data
  const [wheelData, setWheelData] = useState(() => {
    const saved = localStorage.getItem('interactiveWheelData');
    return saved ? JSON.parse(saved) : {
      situation: '',
      self: {
        needs: [],
        feelingsMet: [],
        feelingsUnmet: [],
        strategies: []
      },
      other: {
        needs: [],
        feelingsMet: [],
        feelingsUnmet: [],
        strategies: []
      },
      mutualStrategy: '',
      insights: ''
    };
  });

  // Save progress
  const saveProgress = () => {
    localStorage.setItem('interactiveWheelData', JSON.stringify(wheelData));
    toast.success('Progress saved!');
  };

  const resetAll = () => {
    if (window.confirm('Reset all data? This cannot be undone.')) {
      setWheelData({
        situation: '',
        self: { needs: [], feelingsMet: [], feelingsUnmet: [], strategies: [] },
        other: { needs: [], feelingsMet: [], feelingsUnmet: [], strategies: [] },
        mutualStrategy: '',
        insights: ''
      });
      localStorage.removeItem('interactiveWheelData');
      setShowFinalWheel(false);
      toast.success('All data reset!');
    }
  };

  // Add item to a category
  const addItem = (person, category) => {
    if (!newItemText.trim()) return;
    setWheelData(prev => ({
      ...prev,
      [person]: {
        ...prev[person],
        [category]: [...prev[person][category], newItemText.trim()]
      }
    }));
    setNewItemText('');
  };

  // Remove item
  const removeItem = (person, category, index) => {
    setWheelData(prev => ({
      ...prev,
      [person]: {
        ...prev[person],
        [category]: prev[person][category].filter((_, i) => i !== index)
      }
    }));
  };

  // Get items for display
  const getItems = (person, category) => wheelData[person]?.[category] || [];

  // Calculate positions around a ring
  const getItemPositions = (items, radius, startAngle = -90, spread = 180) => {
    if (items.length === 0) return [];
    const angleStep = items.length > 1 ? spread / (items.length - 1) : 0;
    return items.map((item, i) => {
      const angle = startAngle + (items.length > 1 ? i * angleStep : 0);
      const rad = (angle * Math.PI) / 180;
      return {
        x: 300 + radius * Math.cos(rad),
        y: 300 + radius * Math.sin(rad),
        text: item,
        angle
      };
    });
  };

  // Interactive Wheel Component
  const InteractiveWheelSVG = ({ interactive = true }) => {
    const selfNeeds = getItemPositions(getItems('self', 'needs'), 100, -150, 120);
    const otherNeeds = getItemPositions(getItems('other', 'needs'), 100, 30, 120);
    const selfFeelingsMet = getItemPositions(getItems('self', 'feelingsMet'), 160, -170, 80);
    const selfFeelingsUnmet = getItemPositions(getItems('self', 'feelingsUnmet'), 160, -90, 80);
    const otherFeelingsMet = getItemPositions(getItems('other', 'feelingsMet'), 160, 10, 80);
    const otherFeelingsUnmet = getItemPositions(getItems('other', 'feelingsUnmet'), 160, 90, 80);
    const selfStrategies = getItemPositions(getItems('self', 'strategies'), 230, -170, 100);
    const otherStrategies = getItemPositions(getItems('other', 'strategies'), 230, 70, 100);

    return (
      <svg viewBox="0 0 600 600" className="w-full h-full">
        {/* Definitions */}
        <defs>
          <radialGradient id="centerGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#EC4899" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </radialGradient>
          <linearGradient id="needsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22C55E" />
            <stop offset="100%" stopColor="#10B981" />
          </linearGradient>
          <linearGradient id="feelingsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F43F5E" />
            <stop offset="50%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#22C55E" />
          </linearGradient>
          <linearGradient id="strategiesGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#D97706" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Background glow */}
        <circle cx="300" cy="300" r="280" fill="url(#strategiesGrad)" opacity="0.1" />

        {/* Outer Ring - Strategies (Gold) */}
        <circle 
          cx="300" cy="300" r="260" 
          fill="none" 
          stroke="url(#strategiesGrad)" 
          strokeWidth={activeRing === 'strategies' ? 50 : 40} 
          opacity={activeRing === 'strategies' ? 0.4 : 0.2}
          className={interactive ? "cursor-pointer transition-all duration-300" : ""}
          onClick={() => interactive && setActiveRing('strategies')}
        />
        <circle cx="300" cy="300" r="260" fill="none" stroke="#F59E0B" strokeWidth="2" opacity="0.6" />

        {/* Middle Ring - Feelings (Red/Amber) */}
        <circle 
          cx="300" cy="300" r="190" 
          fill="none" 
          stroke="url(#feelingsGrad)" 
          strokeWidth={activeRing === 'feelings' ? 50 : 40} 
          opacity={activeRing === 'feelings' ? 0.4 : 0.2}
          className={interactive ? "cursor-pointer transition-all duration-300" : ""}
          onClick={() => interactive && setActiveRing('feelings')}
        />
        <circle cx="300" cy="300" r="190" fill="none" stroke="#F43F5E" strokeWidth="2" opacity="0.6" />

        {/* Inner Ring - Needs (Green) */}
        <circle 
          cx="300" cy="300" r="120" 
          fill="none" 
          stroke="url(#needsGrad)" 
          strokeWidth={activeRing === 'needs' ? 45 : 35} 
          opacity={activeRing === 'needs' ? 0.4 : 0.2}
          className={interactive ? "cursor-pointer transition-all duration-300" : ""}
          onClick={() => interactive && setActiveRing('needs')}
        />
        <circle cx="300" cy="300" r="120" fill="none" stroke="#22C55E" strokeWidth="2" opacity="0.6" />

        {/* Center - Connection */}
        <circle cx="300" cy="300" r="50" fill="url(#centerGrad)" filter="url(#glow)" />
        <text x="300" y="295" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">Connection</text>
        <text x="300" y="310" textAnchor="middle" fill="white" fontSize="9">&amp; Belonging</text>

        {/* Division line */}
        <line x1="300" y1="20" x2="300" y2="580" stroke="#64748b" strokeWidth="1" strokeDasharray="5,5" opacity="0.3" />

        {/* Labels for sides */}
        <text x="150" y="35" textAnchor="middle" fill="#22C55E" fontSize="12" fontWeight="bold">MY PERSPECTIVE</text>
        <text x="450" y="35" textAnchor="middle" fill="#8B5CF6" fontSize="12" fontWeight="bold">THEIR PERSPECTIVE</text>

        {/* Ring Labels */}
        <text x="300" y="565" textAnchor="middle" fill="#F59E0B" fontSize="10" fontWeight="bold">STRATEGIES (GOLD)</text>
        <text x="300" y="495" textAnchor="middle" fill="#F43F5E" fontSize="9" fontWeight="bold">FEELINGS (RED)</text>
        <text x="300" y="425" textAnchor="middle" fill="#22C55E" fontSize="9" fontWeight="bold">NEEDS (GREEN)</text>

        {/* Self Needs Items */}
        {selfNeeds.map((item, i) => (
          <g key={`sn-${i}`} className="animate-fadeIn">
            <circle cx={item.x} cy={item.y} r="25" fill="#22C55E" opacity="0.9" />
            <text x={item.x} y={item.y} textAnchor="middle" dy="4" fill="white" fontSize="7" fontWeight="bold">
              {item.text.length > 12 ? item.text.slice(0, 10) + '...' : item.text}
            </text>
          </g>
        ))}

        {/* Other Needs Items */}
        {otherNeeds.map((item, i) => (
          <g key={`on-${i}`} className="animate-fadeIn">
            <circle cx={item.x} cy={item.y} r="25" fill="#8B5CF6" opacity="0.9" />
            <text x={item.x} y={item.y} textAnchor="middle" dy="4" fill="white" fontSize="7" fontWeight="bold">
              {item.text.length > 12 ? item.text.slice(0, 10) + '...' : item.text}
            </text>
          </g>
        ))}

        {/* Self Feelings Met */}
        {selfFeelingsMet.map((item, i) => (
          <g key={`sfm-${i}`} className="animate-fadeIn">
            <circle cx={item.x} cy={item.y} r="22" fill="#22C55E" opacity="0.8" />
            <text x={item.x} y={item.y - 3} textAnchor="middle" fill="white" fontSize="6">😊</text>
            <text x={item.x} y={item.y + 8} textAnchor="middle" fill="white" fontSize="6">
              {item.text.length > 10 ? item.text.slice(0, 8) + '..' : item.text}
            </text>
          </g>
        ))}

        {/* Self Feelings Unmet */}
        {selfFeelingsUnmet.map((item, i) => (
          <g key={`sfu-${i}`} className="animate-fadeIn">
            <circle cx={item.x} cy={item.y} r="22" fill="#F43F5E" opacity="0.8" />
            <text x={item.x} y={item.y - 3} textAnchor="middle" fill="white" fontSize="6">😔</text>
            <text x={item.x} y={item.y + 8} textAnchor="middle" fill="white" fontSize="6">
              {item.text.length > 10 ? item.text.slice(0, 8) + '..' : item.text}
            </text>
          </g>
        ))}

        {/* Other Feelings Met */}
        {otherFeelingsMet.map((item, i) => (
          <g key={`ofm-${i}`} className="animate-fadeIn">
            <circle cx={item.x} cy={item.y} r="22" fill="#A855F7" opacity="0.8" />
            <text x={item.x} y={item.y - 3} textAnchor="middle" fill="white" fontSize="6">😊</text>
            <text x={item.x} y={item.y + 8} textAnchor="middle" fill="white" fontSize="6">
              {item.text.length > 10 ? item.text.slice(0, 8) + '..' : item.text}
            </text>
          </g>
        ))}

        {/* Other Feelings Unmet */}
        {otherFeelingsUnmet.map((item, i) => (
          <g key={`ofu-${i}`} className="animate-fadeIn">
            <circle cx={item.x} cy={item.y} r="22" fill="#EC4899" opacity="0.8" />
            <text x={item.x} y={item.y - 3} textAnchor="middle" fill="white" fontSize="6">😔</text>
            <text x={item.x} y={item.y + 8} textAnchor="middle" fill="white" fontSize="6">
              {item.text.length > 10 ? item.text.slice(0, 8) + '..' : item.text}
            </text>
          </g>
        ))}

        {/* Self Strategies */}
        {selfStrategies.map((item, i) => (
          <g key={`ss-${i}`} className="animate-fadeIn">
            <rect x={item.x - 30} y={item.y - 12} width="60" height="24" rx="12" fill="#F59E0B" opacity="0.9" />
            <text x={item.x} y={item.y + 4} textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">
              {item.text.length > 12 ? item.text.slice(0, 10) + '..' : item.text}
            </text>
          </g>
        ))}

        {/* Other Strategies */}
        {otherStrategies.map((item, i) => (
          <g key={`os-${i}`} className="animate-fadeIn">
            <rect x={item.x - 30} y={item.y - 12} width="60" height="24" rx="12" fill="#D97706" opacity="0.9" />
            <text x={item.x} y={item.y + 4} textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">
              {item.text.length > 12 ? item.text.slice(0, 10) + '..' : item.text}
            </text>
          </g>
        ))}

        {/* Click hints when empty */}
        {interactive && getItems('self', 'needs').length === 0 && getItems('other', 'needs').length === 0 && (
          <>
            <text x="300" y="385" textAnchor="middle" fill="#22C55E" fontSize="9" opacity="0.7">👆 Click rings to add items</text>
          </>
        )}
      </svg>
    );
  };

  // Input Panel Component
  const InputPanel = () => {
    if (!activeRing) return null;

    const ringConfig = {
      needs: {
        title: '🌱 Needs (The Green)',
        description: 'What needs are alive for you / them?',
        color: 'emerald',
        selfCategory: 'needs',
        otherCategory: 'needs'
      },
      feelings: {
        title: '❤️ Feelings (The Red)',
        description: 'What feelings arise when needs are met or unmet?',
        color: 'rose',
        selfCategories: ['feelingsMet', 'feelingsUnmet'],
        otherCategories: ['feelingsMet', 'feelingsUnmet']
      },
      strategies: {
        title: '🏆 Strategies (The Gold)',
        description: 'What strategies or requests could meet these needs?',
        color: 'amber',
        selfCategory: 'strategies',
        otherCategory: 'strategies'
      }
    };

    const config = ringConfig[activeRing];
    const colorClasses = {
      emerald: 'from-emerald-900/90 to-green-900/90 border-emerald-500',
      rose: 'from-rose-900/90 to-red-900/90 border-rose-500',
      amber: 'from-amber-900/90 to-orange-900/90 border-amber-500'
    };

    return (
      <div className={`fixed bottom-20 left-1/2 -translate-x-1/2 w-full max-w-2xl bg-gradient-to-br ${colorClasses[config.color]} border-2 rounded-2xl p-6 shadow-2xl z-50 animate-in slide-in-from-bottom duration-300`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white">{config.title}</h3>
            <p className="text-slate-300 text-sm">{config.description}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setActiveRing(null)} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Person Toggle */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activePerson === 'self' ? 'default' : 'outline'}
            onClick={() => setActivePerson('self')}
            className={activePerson === 'self' ? 'bg-emerald-600' : 'border-slate-500 text-slate-300'}
          >
            💚 My Perspective
          </Button>
          <Button
            variant={activePerson === 'other' ? 'default' : 'outline'}
            onClick={() => setActivePerson('other')}
            className={activePerson === 'other' ? 'bg-purple-600' : 'border-slate-500 text-slate-300'}
          >
            💜 Their Perspective
          </Button>
        </div>

        {activeRing === 'feelings' ? (
          <div className="grid grid-cols-2 gap-4">
            {/* Feelings Met */}
            <div>
              <label className="text-sm font-bold text-amber-300 mb-2 block">😊 When Needs MET:</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  placeholder="Add feeling..."
                  className="bg-slate-800/50 border-amber-700/50 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && addItem(activePerson, 'feelingsMet')}
                />
                <Button onClick={() => addItem(activePerson, 'feelingsMet')} size="sm" className="bg-amber-600 hover:bg-amber-500">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {getItems(activePerson, 'feelingsMet').map((item, i) => (
                  <span key={i} className="bg-amber-600/50 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    {item}
                    <button onClick={() => removeItem(activePerson, 'feelingsMet', i)} className="hover:text-red-300">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            {/* Feelings Unmet */}
            <div>
              <label className="text-sm font-bold text-rose-300 mb-2 block">😔 When Needs NOT Met:</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  placeholder="Add feeling..."
                  className="bg-slate-800/50 border-rose-700/50 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && addItem(activePerson, 'feelingsUnmet')}
                />
                <Button onClick={() => addItem(activePerson, 'feelingsUnmet')} size="sm" className="bg-rose-600 hover:bg-rose-500">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {getItems(activePerson, 'feelingsUnmet').map((item, i) => (
                  <span key={i} className="bg-rose-600/50 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    {item}
                    <button onClick={() => removeItem(activePerson, 'feelingsUnmet', i)} className="hover:text-red-300">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex gap-2 mb-3">
              <Input
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                placeholder={`Add ${activeRing === 'needs' ? 'need' : 'strategy'}...`}
                className="bg-slate-800/50 border-slate-600 text-white flex-1"
                onKeyDown={(e) => e.key === 'Enter' && addItem(activePerson, config.selfCategory)}
              />
              <Button onClick={() => addItem(activePerson, config.selfCategory)} className={activeRing === 'needs' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600 hover:bg-amber-500'}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {getItems(activePerson, config.selfCategory).map((item, i) => (
                <span key={i} className={`${activeRing === 'needs' ? 'bg-emerald-600/50' : 'bg-amber-600/50'} text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-2`}>
                  {item}
                  <button onClick={() => removeItem(activePerson, config.selfCategory, i)} className="hover:text-red-300">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Quick tip */}
        <div className="mt-4 pt-4 border-t border-slate-700/50">
          <p className="text-slate-400 text-xs italic">
            💡 Tip: Click the Feelings & Needs Tool button for vocabulary inspiration
          </p>
        </div>
      </div>
    );
  };

  // Pulsing CTA
  const FeelingsNeedsCTA = ({ className = '' }) => (
    <Button
      onClick={() => setIsHelperOpen(true)}
      className={`relative bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white ${className}`}
    >
      <div className="absolute inset-0 rounded-md bg-rose-500/30 animate-ping" style={{ animationDuration: '2s' }} />
      <span className="relative flex items-center gap-2">
        <FeelIcon size={18} />
        Feelings & Needs Tool
        <NeedIcon size={18} />
      </span>
    </Button>
  );

  // Render based on view state
  const renderContent = () => {
    if (showFinalWheel) {
      return (
        <div className="space-y-6 animate-fadeIn">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-2">🏆 Your Completed Wheel</h2>
            <p className="text-emerald-300">All perspectives mapped together</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <InteractiveWheelSVG interactive={false} />
          </div>

          {/* Mutual Strategy Input */}
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-amber-900/50 to-yellow-900/40 border-2 border-amber-500/50 rounded-2xl p-6">
            <label className="text-xl font-bold text-amber-300 mb-3 block">✨ The Gold: Mutual Strategy</label>
            <Textarea
              value={wheelData.mutualStrategy}
              onChange={(e) => setWheelData(prev => ({ ...prev, mutualStrategy: e.target.value }))}
              placeholder="What strategy honors BOTH sets of needs? What can we both say yes to?"
              className="bg-amber-900/30 border-amber-500/40 text-white min-h-[100px] text-lg"
            />
          </div>

          {/* Insights */}
          <div className="max-w-2xl mx-auto bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <label className="text-lg font-bold text-purple-300 mb-3 block">💡 Key Insights</label>
            <Textarea
              value={wheelData.insights}
              onChange={(e) => setWheelData(prev => ({ ...prev, insights: e.target.value }))}
              placeholder="What did you discover? Where do the needs overlap?"
              className="bg-slate-900/50 border-slate-600 text-white min-h-[80px]"
            />
          </div>

          <div className="flex justify-center gap-4">
            <Button onClick={() => setShowFinalWheel(false)} variant="outline" className="border-slate-600 text-slate-300">
              <ArrowLeftIcon size={18} className="mr-2" /> Back to Edit
            </Button>
            <Button onClick={() => navigate('/workbook/empathetic-alchemy')} className="bg-purple-600 hover:bg-purple-500">
              Try Empathetic Alchemy <ArrowRightIcon size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Situation Input */}
        <div className="max-w-2xl mx-auto bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
          <label className="text-lg font-bold text-slate-200 mb-2 block">🎯 What's the situation or conflict?</label>
          <Textarea
            value={wheelData.situation}
            onChange={(e) => setWheelData(prev => ({ ...prev, situation: e.target.value }))}
            placeholder="Describe what's happening..."
            className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
          />
        </div>

        {/* The Wheel */}
        <div className="relative max-w-2xl mx-auto">
          <InteractiveWheelSVG interactive={true} />
          
          {/* Legend */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-4 text-xs">
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-emerald-500" /> Needs</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-rose-500" /> Feelings</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-amber-500" /> Strategies</span>
          </div>
        </div>

        {/* Instructions */}
        <div className="max-w-2xl mx-auto text-center space-y-3">
          <p className="text-slate-300">
            👆 <span className="text-emerald-300 font-semibold">Click any ring</span> to add items. They'll appear in the wheel!
          </p>
          <div className="flex justify-center gap-4">
            <FeelingsNeedsCTA />
          </div>
        </div>

        {/* Generate Final Wheel Button */}
        {(getItems('self', 'needs').length > 0 || getItems('other', 'needs').length > 0) && (
          <div className="flex justify-center mt-6">
            <Button 
              onClick={() => setShowFinalWheel(true)}
              className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-white text-lg px-8 py-6 rounded-full"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              See Complete Wheel & Find The Gold
              <ArrowRightIcon size={20} className="ml-2" />
            </Button>
          </div>
        )}

        {/* Wisdom Reminder */}
        <div className="max-w-2xl mx-auto bg-gradient-to-r from-purple-900/30 to-pink-900/20 border border-purple-500/30 rounded-xl p-4 text-center">
          <p className="text-purple-200 italic">
            "Drop into the needs, it will get you out of the weeds." 🌱
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeftIcon size={16} className="mr-1" /> Back
              </Button>
              <div className="h-5 w-px bg-slate-700" />
              <div>
                <h1 className="text-lg font-bold text-white">Interactive Communication Wheel</h1>
                <p className="text-xs text-emerald-400">Page 25 • Click the rings to build your wheel!</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button onClick={saveProgress} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <SaveIcon size={16} className="mr-1" /> Save
              </Button>
              <Button onClick={resetAll} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8 pb-32">
        {renderContent()}
      </main>

      {/* Input Panel */}
      <InputPanel />

      {/* Floating Helper */}
      <FloatingFeelingsHelper />
      <FeelingsNeedsHelper isOpen={isHelperOpen} onClose={() => setIsHelperOpen(false)} />
    </div>
  );
}
